/*
 * pdp-reviews.js — masonry layout, sorting, filtering and "Load more" for the Customer Reviews section.
 * Vanilla JS, no reload, no network requests: it re-orders, shows/hides and positions the review <li>
 * elements that Liquid already rendered (so the reviews stay in the HTML for SEO and no-JS users;
 * without JS the cards fall back to a CSS multi-column layout).
 *
 *   <pdp-reviews data-page-size="8" data-max-columns="4">
 *     [data-pdp-panel-toggle]    <button aria-expanded>  opens / closes the rating breakdown panel
 *     [data-pdp-panel]           the panel (rating chart, sort, "With photos")
 *     [data-pdp-sort]            <select>  newest | highest | lowest
 *     [data-pdp-star]            <button>  filter by star rating (data-pdp-star="5"…"1"), toggles
 *     [data-pdp-photos]          <button aria-pressed>  "With photos" filter
 *     [data-pdp-clear]           <button>  reset filters
 *     [data-pdp-statusrow]       wrapper of the status line + clear button (gets data-filtered)
 *     [data-pdp-list]            <ul>      the review <li data-pdp-review data-rating data-date data-photo data-index>
 *     [data-pdp-more]            <button>  Load more
 *     [data-pdp-status]          live region: "Showing 8 of 23 reviews"
 *     [data-pdp-empty]           message when filters match nothing
 *
 * Option B (reviews app): <button data-pdp-app-form="CSS selector"> clicks the app's own
 * "write a review" button so the app's form opens.
 */
(() => {
  if (window.__pdpReviews) return;
  window.__pdpReviews = true;

  const $ = (root, selector) => root.querySelector(selector);
  const $$ = (root, selector) => Array.from(root.querySelectorAll(selector));
  const fill = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

  // A card is never narrower than this (15rem), so the number of columns follows the available width.
  const MIN_CARD_REM = 15;

  class PdpReviews extends HTMLElement {
    connectedCallback() {
      this.list = $(this, '[data-pdp-list]');
      if (!this.list) return;

      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 8);
      this.maxColumns = Math.min(4, Math.max(1, Number(this.dataset.maxColumns) || 4));
      this.state = { sort: 'newest', star: 0, photos: false, shown: this.pageSize };

      this.items = $$(this.list, '[data-pdp-review]').map((el) => ({
        el,
        rating: Number(el.dataset.rating) || 0,
        date: Number(el.dataset.date) || 0,
        photo: el.dataset.photo === 'true',
        index: Number(el.dataset.index) || 0,
      }));

      this.sortEl = $(this, '[data-pdp-sort]');
      this.moreEl = $(this, '[data-pdp-more]');
      this.statusEl = $(this, '[data-pdp-status]');
      this.rowEl = $(this, '[data-pdp-statusrow]');
      this.emptyEl = $(this, '[data-pdp-empty]');
      this.clearEls = $$(this, '[data-pdp-clear]');
      this.starEls = $$(this, '[data-pdp-star]');
      this.photosEl = $(this, '[data-pdp-photos]');
      this.panelEl = $(this, '[data-pdp-panel]');

      this.layoutQueued = false;
      this.queueLayout = this.queueLayout.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);

      // Re-position the cards whenever the grid width or a card's height changes
      // (window resize, images and fonts loading, text wrapping).
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.queueLayout);
        this.resizeObserver.observe(this.list);
        this.items.forEach((item) => this.resizeObserver.observe(item.el));
      } else {
        window.addEventListener('resize', this.queueLayout);
      }
      this.list.addEventListener('load', this.queueLayout, true);
      window.addEventListener('load', this.queueLayout);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.queueLayout);

      this.apply({ focusFirstNew: false });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      window.removeEventListener('resize', this.queueLayout);
      window.removeEventListener('load', this.queueLayout);
      if (this.list) this.list.removeEventListener('load', this.queueLayout, true);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    onChange(event) {
      if (event.target === this.sortEl) {
        this.state.sort = this.sortEl.value;
        this.state.shown = this.pageSize;
        this.apply({ focusFirstNew: false });
      }
    }

    onClick(event) {
      const toggle = event.target.closest('[data-pdp-panel-toggle]');
      if (toggle && this.contains(toggle) && this.panelEl) {
        const open = this.panelEl.hidden;
        this.panelEl.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
        return;
      }

      const star = event.target.closest('[data-pdp-star]');
      if (star && this.contains(star)) {
        const value = Number(star.dataset.pdpStar);
        this.state.star = this.state.star === value ? 0 : value;
        this.state.shown = this.pageSize;
        this.apply({ focusFirstNew: false });
        return;
      }

      const photos = event.target.closest('[data-pdp-photos]');
      if (photos && this.contains(photos)) {
        this.state.photos = !this.state.photos;
        this.state.shown = this.pageSize;
        this.apply({ focusFirstNew: false });
        return;
      }

      if (event.target.closest('[data-pdp-clear]')) {
        this.state.star = 0;
        this.state.photos = false;
        this.state.shown = this.pageSize;
        this.apply({ focusFirstNew: false });
        return;
      }

      if (event.target.closest('[data-pdp-more]')) {
        this.state.shown += this.pageSize;
        this.apply({ focusFirstNew: true });
      }
    }

    sorted() {
      const { sort } = this.state;
      const copy = this.items.slice();
      copy.sort((a, b) => {
        if (sort === 'highest') return b.rating - a.rating || b.date - a.date || a.index - b.index;
        if (sort === 'lowest') return a.rating - b.rating || b.date - a.date || a.index - b.index;
        return b.date - a.date || a.index - b.index; // newest
      });
      return copy;
    }

    apply({ focusFirstNew }) {
      const { star, photos, shown } = this.state;
      const ordered = this.sorted();
      const matches = ordered.filter((item) => (!star || item.rating === star) && (!photos || item.photo));
      const previouslyVisible = this.items.filter((item) => !item.el.hidden).length;

      // Re-order the DOM to the chosen sort (moves nodes, no re-render).
      this.list.append(...ordered.map((item) => item.el));

      ordered.forEach((item) => {
        const matchIndex = matches.indexOf(item);
        item.el.hidden = matchIndex === -1 || matchIndex >= shown;
      });

      const visible = Math.min(shown, matches.length);
      const filtered = star > 0 || photos;

      if (this.moreEl) this.moreEl.hidden = matches.length <= shown;
      if (this.emptyEl) this.emptyEl.hidden = matches.length > 0;
      this.clearEls.forEach((el) => (el.hidden = !filtered));
      if (this.rowEl) this.rowEl.dataset.filtered = String(filtered);

      this.starEls.forEach((el) => el.setAttribute('aria-pressed', String(Number(el.dataset.pdpStar) === star)));
      if (this.photosEl) this.photosEl.setAttribute('aria-pressed', String(photos));

      if (this.statusEl) {
        this.statusEl.textContent = matches.length
          ? fill(this.dataset.statusText || 'Showing {shown} of {total} reviews', { shown: visible, total: matches.length })
          : '';
      }

      this.layout();

      // Keyboard / screen-reader users: move focus to the first review that was just revealed.
      if (focusFirstNew && matches[previouslyVisible] && !matches[previouslyVisible].el.hidden) {
        matches[previouslyVisible].el.focus({ preventScroll: false });
      }
    }

    queueLayout() {
      if (this.layoutQueued) return;
      this.layoutQueued = true;
      requestAnimationFrame(() => {
        this.layoutQueued = false;
        this.layout();
      });
    }

    // Masonry: cards keep their DOM order (left to right, row by row); each one goes into the
    // currently shortest column. Cards are absolutely positioned inside the <ul>.
    layout() {
      const list = this.list;
      const width = list.clientWidth;
      if (!list || !width) return;

      const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const gap = parseFloat(getComputedStyle(list).columnGap) || 16;
      const minWidth = MIN_CARD_REM * rootFont;
      const columns = Math.max(1, Math.min(this.maxColumns, Math.floor((width + gap) / (minWidth + gap))));
      const columnWidth = (width - gap * (columns - 1)) / columns;

      list.classList.add('is-masonry');
      const cards = $$(list, '[data-pdp-review]').filter((el) => !el.hidden);
      cards.forEach((el) => {
        el.style.width = columnWidth + 'px';
      });

      const heights = new Array(columns).fill(0);
      cards.forEach((el) => {
        let column = 0;
        for (let i = 1; i < columns; i += 1) {
          if (heights[i] < heights[column] - 0.5) column = i;
        }
        el.style.left = column * (columnWidth + gap) + 'px';
        el.style.top = heights[column] + 'px';
        heights[column] += el.offsetHeight + gap;
      });

      list.style.height = Math.max(0, Math.max(...heights) - gap) + 'px';
      // Animate re-ordering only after the first layout has been painted.
      if (!list.classList.contains('is-ready')) requestAnimationFrame(() => list.classList.add('is-ready'));
    }
  }

  // Option B (reviews app): "Write a review" clicks the app's own button so its form opens.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-pdp-app-form]');
    if (!trigger) return;
    const appButton = trigger.dataset.pdpAppForm && document.querySelector(trigger.dataset.pdpAppForm);
    if (appButton) {
      appButton.click();
      return;
    }
    // App not loaded (or the selector is wrong): bring the app widget into view instead.
    const slot = document.querySelector('[data-pdp-app-slot]');
    if (slot) slot.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  if (!customElements.get('pdp-reviews')) customElements.define('pdp-reviews', PdpReviews);
})();
